/**
 * LINKURIOUS CONFIDENTIAL
 * Copyright Linkurious SAS 2012 - 2018
 *
 * Created by andrebarata on 2018-11-29.
 */

'use strict';

import {
  Directive,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import { Tools } from 'linkurious-shared';

@Directive({
  selector: '[search-navigation]'
})
export class SearchNavigationDirective implements OnInit, OnDestroy {

  @Output() submit: EventEmitter<{
    source: 'keyboard' | 'mouse',
    index: number
  }> = new EventEmitter();

  private _domListener: MutationObserver;
  private _input: HTMLElement;
  private _itemsList: Array<HTMLElement>;
  private _currentIndex: number = -1;

  constructor(
    private _render: Renderer2,
    private _elementRef: ElementRef
  ) {
  }

  ngOnInit() {
    this._input = this._elementRef.nativeElement.querySelector('.search__input');
    this._itemsList = this.itemsList;

    this._domListener = new MutationObserver(() => {
      this.resetNavigation();
      this._itemsList = this.itemsList;
    });
    this._domListener.observe(this._elementRef.nativeElement, {childList: true});
    document.addEventListener('click', this.mouseClick.bind(this));
    this._elementRef.nativeElement.addEventListener('keydown', this.keyDown.bind(this));
    document.addEventListener('mousemove', this.mouseMove.bind(this));
  }

  ngOnDestroy() {
    this._domListener.disconnect();
    document.removeEventListener('click', this.mouseClick.bind(this));
    this._elementRef.nativeElement.removeEventListener('keydown', this.keyDown.bind(this));
    document.removeEventListener('mousemove', this.mouseMove.bind(this));
  }

  /*
  * Listening mouse click to submit action
  */
  private mouseClick(event: MouseEvent) {
    if (
      Tools.isDefined(this._elementRef.nativeElement.querySelector('.search__list')) &&
      this._elementRef.nativeElement.querySelector('.search__list').contains(event.target)
    ) {
      this.submitAction('mouse');
    }
    // click outside
    if(!this._elementRef.nativeElement.contains(event.target)) {
      this.blurInput();
    }
  }

  /*
  * Listening mouse moves to focus and get current index or reset navigation
  */
  private mouseMove(event: MouseEvent) {
    if (
      Tools.isDefined(this._elementRef.nativeElement.querySelector('.search__list')) &&
      this._elementRef.nativeElement.querySelector('.search__list').contains(event.target)
    ) {
      this._currentIndex = Array
        .from( this._elementRef.nativeElement.querySelector('ul').children)
        .indexOf(this._elementRef.nativeElement.querySelector('li:hover'));
      if(this._currentIndex >= 0) {
        this._itemsList[this._currentIndex].focus();
      }
    } else if (
      Tools.isDefined(this._itemsList) &&
      this._currentIndex > -1
    ) {
      this.resetNavigation();
    }
  }

  /*
  * Listening keyboard events
  */
  private keyDown(event: KeyboardEvent) {
    // Avoid triggering the navigation if the list is not focused and it's none of the desired keys
    if ( this._elementRef.nativeElement.contains(document.activeElement) ) {
      event.stopPropagation();
      if(Tools.isDefined(this._itemsList)) {
        switch (event.keyCode) {
          case 38 : // Arrow up
            event.preventDefault();
            this.focusUp();
            break;

          case 40 : // Arrow down
            event.preventDefault();
            this.focusDown();
            break;

          case 13 : // Enter
            event.preventDefault();
            this.submitAction('keyboard');
            break;

          default :
            this.focusInput();
            break;
        }
      }
    }
  }

  /*
  * Get the Element (ul) children list
  */
  private get itemsList(): Array<HTMLElement> {
    if(Tools.isDefined(this._elementRef.nativeElement.querySelector('.search__list'))) {
      return this._elementRef.nativeElement.querySelector('.search__list').children
    }
    return undefined;
  }

  /*
  * Focus the previous item
  */
  private focusUp() {
    if(this._currentIndex > 0) {
      this._currentIndex--;
      this._itemsList[this._currentIndex].focus()
    } else {
      this.focusInput();
    }
  }

  /*
  * Focus the next item
  */
  private focusDown() {
    const itemsListLastIndex = this._elementRef.nativeElement.querySelector('.search__list').children.length - 1;
    if(this._currentIndex < itemsListLastIndex) {
      this._currentIndex++;
      this._itemsList[this._currentIndex].focus();
    }
  }

  /*
  * Focus the input when press ESC or press up on the first item
  */
  private focusInput() {
    this._input.focus();
    this.resetNavigation();
  }

  /*
  * Blur the input when click outside search
  */
  private blurInput() {
    this._input.blur();
    this.resetNavigation();
  }

  /*
  * Emit the id when mouse click in one item or press enter / tab
  */
  private submitAction(source: 'keyboard' | 'mouse') {
    if(this._currentIndex > -1 && Tools.isDefined(this._itemsList[this._currentIndex])) {
      this.submit.emit({source: source, index: this._currentIndex});
    }
  }

  /*
  * Reset navigation
  */
  private resetNavigation() {
    if(this._currentIndex > -1 && Tools.isDefined(this._itemsList[this._currentIndex])) {
      this._itemsList[this._currentIndex].blur();
    }
    this._currentIndex = -1;
  }

}
